import Autocomplete from '@mui/material/Autocomplete'
import CircularProgress from '@mui/material/CircularProgress'
import TextField from '@mui/material/TextField'
import axios from 'axios'
import { useEffect, useMemo, useRef, useState } from 'react'

import { constants } from './lazy-select.constant'
import { LazySelectData, LazySelectResponse } from './lazy-select.model'

type Props = {
	id: string
	label: string
	url: string
	limit?: number
	multiple?: boolean
	disabled?: boolean
	defaultValue?: LazySelectData | LazySelectData[]
	placeholder?: string
	onChange?: (id: string, value: LazySelectData | LazySelectData[]) => void
}

const LazySelect = ({
	id,
	label,
	url,
	limit = 30,
	multiple = false,
	disabled = false,
	defaultValue,
	placeholder,
	onChange,
}: Props) => {
	const [value, setValue] = useState<LazySelectData | null>(null)
	const [multipleValue, setMultipleValue] = useState<LazySelectData[]>([])
	const [open, setOpen] = useState(false)
	const [options, setOptions] = useState<LazySelectData[]>([])
	const [loading, setLoading] = useState(false)
	const [inputValue, setInputValue] = useState('')

	const debounceTimer = useRef<number | null>(null)
	const activeRequestId = useRef(0)

	useEffect(() => {
		if (defaultValue === undefined) {
			return
		}

		if (multiple && Array.isArray(defaultValue)) {
			setMultipleValue(defaultValue)
		} else if (!multiple && !Array.isArray(defaultValue) && defaultValue.value !== '') {
			setValue(defaultValue)
		}
	}, [])

	const fetchOptions = async (query: string) => {
		const requestId = activeRequestId.current + 1
		activeRequestId.current = requestId
		setLoading(true)

		try {
			const requestUrl = query === '' ? url : `${url}&q=${encodeURIComponent(query)}`
			const response = await axios.get<LazySelectResponse>(requestUrl, {
				headers: { 'X-Requested-With': 'XMLHttpRequest' },
			})

			if (requestId !== activeRequestId.current) {
				return
			}

			const payload = response.data.lazyselect
			if (payload === undefined) {
				setOptions([])
				return
			}

			setOptions(
				payload.items.map((item) => ({
					label: item.label,
					value: String(item.value),
				})),
			)
		} catch (e) {
			if (requestId === activeRequestId.current) {
				setOptions([])
			}
			console.error('LazySelect: failed to load options', e)
		} finally {
			if (requestId === activeRequestId.current) {
				setLoading(false)
			}
		}
	}

	useEffect(() => {
		if (!open) {
			return
		}

		if (debounceTimer.current !== null) {
			window.clearTimeout(debounceTimer.current)
		}

		debounceTimer.current = window.setTimeout(() => {
			fetchOptions(inputValue)
		}, constants.debounceMs)

		return () => {
			if (debounceTimer.current !== null) {
				window.clearTimeout(debounceTimer.current)
			}
		}
	}, [inputValue, open])

	useEffect(() => {
		if (open && options.length === 0 && !loading) {
			fetchOptions('')
		}
	}, [open])

	const mergedOptions = useMemo(() => {
		const selectedValues = multiple ? multipleValue : value !== null ? [value] : []
		const existingValues = new Set(options.map((option) => option.value))
		const missing = selectedValues.filter((item) => !existingValues.has(item.value))
		return [...missing, ...options]
	}, [options, value, multipleValue, multiple])

	const handleChange = (_: unknown, newValue: LazySelectData | LazySelectData[] | null) => {
		if (multiple) {
			const arrayValue = Array.isArray(newValue) ? newValue : []
			setMultipleValue(arrayValue)
			onChange?.(id, arrayValue)
		} else {
			const singleValue = !Array.isArray(newValue) && newValue !== null ? newValue : { label: '', value: '' }
			setValue(singleValue.value === '' ? null : singleValue)
			onChange?.(id, singleValue)
		}
	}

	return (
		<Autocomplete
			id={id}
			multiple={multiple}
			disabled={disabled}
			open={open}
			onOpen={() => setOpen(true)}
			onClose={() => setOpen(false)}
			options={mergedOptions}
			value={multiple ? multipleValue : value}
			inputValue={inputValue}
			onInputChange={(_, newInputValue, reason) => {
				if (reason === 'reset') {
					return
				}
				setInputValue(newInputValue)
			}}
			onChange={handleChange}
			getOptionLabel={(option) => option.label}
			isOptionEqualToValue={(option, compared) => option.value === compared.value}
			filterOptions={(x) => x}
			filterSelectedOptions={multiple}
			loading={loading}
			noOptionsText={loading ? 'Loading…' : 'No options'}
			renderInput={(params) => (
				<TextField
					{...params}
					variant="standard"
					label={label}
					placeholder={placeholder}
					InputProps={{
						...params.InputProps,
						endAdornment: (
							<>
								{loading ? <CircularProgress color="inherit" size={16} /> : null}
								{params.InputProps.endAdornment}
							</>
						),
					}}
				/>
			)}
		/>
	)
}

export default LazySelect
